import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Search,
  TrendingUp,
  X,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import { getLocalDateString, parseLocalDate } from '../../utils/dateUtils';
import { isExercisePerformed } from '../../utils/exerciseUtils';
import type { TabType, Workout } from '../../types';

interface ProgressTabProps {
  setActiveTab: (tab: TabType) => void;
}

interface ExerciseProgressPoint {
  date: string;
  peso: number;
  oneRM: number;
  volumen: number;
}

function calculateEstimatedOneRM(weight: number, reps: number): number {
  if (weight <= 0 || reps <= 0) return 0;
  if (reps === 1) return weight;
  return weight * (1 + reps / 30);
}

function getWeekStartDate(date: Date): Date {
  const day = date.getDay();
  const diff = (day + 6) % 7;
  const start = new Date(date);
  start.setDate(date.getDate() - diff);
  start.setHours(0, 0, 0, 0);
  return start;
}

export function ProgressTab({ setActiveTab }: ProgressTabProps) {
  const { darkMode, theme } = useTheme();
  const { workouts } = useData();

  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [isExerciseDropdownOpen, setIsExerciseDropdownOpen] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const weeklyCarouselRef = useRef<HTMLDivElement>(null);
  const [activeWeeklyIndex, setActiveWeeklyIndex] = useState(0);

  const allExercises = useMemo(() => {
    const exercises = new Set<string>();
    workouts.forEach((workout) => {
      workout.exercises.forEach((exercise) => exercises.add(exercise.name));
    });
    return Array.from(exercises).sort((a, b) => a.localeCompare(b, 'es'));
  }, [workouts]);

  const filteredExercises = useMemo(() => {
    const term = exerciseSearch.trim().toLowerCase();
    if (!term) return allExercises;
    return allExercises.filter((exercise) => exercise.toLowerCase().includes(term));
  }, [allExercises, exerciseSearch]);

  useEffect(() => {
    if (selectedExercise) {
      setExerciseSearch(selectedExercise);
    }
  }, [selectedExercise]);

  useEffect(() => {
    if (!isChartModalOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isChartModalOpen]);

  const getExerciseData = useCallback((exerciseName: string): ExerciseProgressPoint[] => {
    const exerciseWorkouts = workouts
      .filter((workout) => workout.exercises.some((exercise) => exercise.name === exerciseName))
      .sort(
        (a, b) => parseLocalDate(a.date).getTime() - parseLocalDate(b.date).getTime()
      );

    return exerciseWorkouts
      .map<ExerciseProgressPoint | null>((workout) => {
        const exercise = workout.exercises.find((entry) => entry.name === exerciseName);
        if (!exercise || !isExercisePerformed(exercise)) {
          return null;
        }

        const maxWeight = Math.max(...exercise.sets.map((set) => set.weight));
        const estimatedOneRM = Math.max(
          ...exercise.sets.map((set) => calculateEstimatedOneRM(set.weight, set.reps))
        );
        const totalVolume = exercise.sets.reduce(
          (sum, set) => sum + set.weight * set.reps,
          0
        );
        const workoutDate = parseLocalDate(workout.date);

        return {
          date: workoutDate.toLocaleDateString('es-ES', {
            month: 'short',
            day: 'numeric',
          }),
          peso: maxWeight,
          oneRM: estimatedOneRM,
          volumen: totalVolume,
        };
      })
      .filter((point): point is ExerciseProgressPoint => point !== null);
  }, [workouts]);

  const selectedExerciseData = useMemo(
    () => (selectedExercise ? getExerciseData(selectedExercise) : []),
    [getExerciseData, selectedExercise]
  );

  const exerciseStats = useMemo(() => {
    if (selectedExerciseData.length === 0) {
      return null;
    }

    const last = selectedExerciseData[selectedExerciseData.length - 1];
    const previous =
      selectedExerciseData.length > 1
        ? selectedExerciseData[selectedExerciseData.length - 2]
        : null;

    return {
      prPeso: Math.max(...selectedExerciseData.map((point) => point.peso)),
      prOneRM: Math.max(...selectedExerciseData.map((point) => point.oneRM)),
      prVolumen: Math.max(...selectedExerciseData.map((point) => point.volumen)),
      lastPeso: last.peso,
      lastOneRM: last.oneRM,
      lastVolumen: last.volumen,
      diffPeso: previous ? last.peso - previous.peso : null,
      diffVolume: previous ? last.volumen - previous.volumen : null,
    };
  }, [selectedExerciseData]);

  const overview = useMemo(() => {
    const sortedWorkouts = [...workouts].sort(
      (a, b) => parseLocalDate(a.date).getTime() - parseLocalDate(b.date).getTime()
    );
    const uniqueDays = new Set(sortedWorkouts.map((workout) => workout.date));
    const totalVolume = sortedWorkouts.reduce((sum, workout) => {
      return sum + workout.exercises.reduce((exerciseSum, exercise) => {
        return exerciseSum + exercise.sets.reduce((setSum, set) => setSum + set.weight * set.reps, 0);
      }, 0);
    }, 0);

    const now = new Date();
    const last28Cutoff = new Date(now);
    last28Cutoff.setDate(now.getDate() - 28);
    const previous28Cutoff = new Date(now);
    previous28Cutoff.setDate(now.getDate() - 56);

    const recentWorkouts = sortedWorkouts.filter(
      (workout) => parseLocalDate(workout.date) >= last28Cutoff
    );
    const previousWorkouts = sortedWorkouts.filter((workout) => {
      const workoutDate = parseLocalDate(workout.date);
      return workoutDate >= previous28Cutoff && workoutDate < last28Cutoff;
    });

    const volumeFor = (items: Workout[]) =>
      items.reduce((sum, workout) => {
        return sum + workout.exercises.reduce((exerciseSum, exercise) => {
          return exerciseSum + exercise.sets.reduce(
            (setSum, set) => setSum + set.weight * set.reps,
            0
          );
        }, 0);
      }, 0);

    const recentVolume = volumeFor(recentWorkouts);
    const previousVolume = volumeFor(previousWorkouts);
    const volumeDelta =
      previousVolume > 0 ? ((recentVolume - previousVolume) / previousVolume) * 100 : null;

    let streak = 0;
    let cursor = new Date();
    while (uniqueDays.has(getLocalDateString(cursor))) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    }

    const lastWorkout = sortedWorkouts[sortedWorkouts.length - 1];

    return {
      totalWorkouts: sortedWorkouts.length,
      activeDays: uniqueDays.size,
      totalVolume,
      recentVolume,
      volumeDelta,
      streak,
      lastWorkoutDate: lastWorkout?.date ?? null,
    };
  }, [workouts]);

  const exerciseMomentum = useMemo(() => {
    return allExercises
      .map((exerciseName) => {
        const points = getExerciseData(exerciseName);
        if (points.length < 2) return null;
        const last = points[points.length - 1];
        const previous = points[points.length - 2];
        const deltaOneRM = last.oneRM - previous.oneRM;
        const deltaVolume = last.volumen - previous.volumen;

        return {
          exerciseName,
          sessions: points.length,
          lastWeight: last.peso,
          lastVolume: last.volumen,
          deltaOneRM,
          deltaVolume,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
      .sort((a, b) => {
        if (b.deltaOneRM !== a.deltaOneRM) return b.deltaOneRM - a.deltaOneRM;
        return b.deltaVolume - a.deltaVolume;
      });
  }, [allExercises, getExerciseData]);

  const topProgressors = useMemo(
    () => exerciseMomentum.filter((entry) => entry.deltaOneRM > 0).slice(0, 4),
    [exerciseMomentum]
  );

  const routineWeeklyVolume = useMemo(() => {
    const byRoutine = new Map<
      string,
      Map<string, { weekKey: string; label: string; total: number; date: Date }>
    >();

    workouts.forEach((workout) => {
      const routineName = workout.routine_name || 'Sin rutina';
      const workoutDate = parseLocalDate(workout.date);
      const weekStart = getWeekStartDate(workoutDate);
      const weekKey = getLocalDateString(weekStart);
      const label = weekStart.toLocaleDateString('es-ES', {
        month: 'short',
        day: 'numeric',
      });
      const totalVolume = workout.exercises.reduce((sum, exercise) => {
        return (
          sum + exercise.sets.reduce((setSum, set) => setSum + set.weight * set.reps, 0)
        );
      }, 0);

      if (!byRoutine.has(routineName)) {
        byRoutine.set(routineName, new Map());
      }

      const routineWeeks = byRoutine.get(routineName);
      if (!routineWeeks) return;

      const existing = routineWeeks.get(weekKey);
      if (existing) {
        existing.total += totalVolume;
        return;
      }

      routineWeeks.set(weekKey, {
        weekKey,
        label,
        total: totalVolume,
        date: weekStart,
      });
    });

    return Array.from(byRoutine.entries())
      .map(([routine, weeks]) => {
        const recent = Array.from(weeks.values())
          .sort((a, b) => a.date.getTime() - b.date.getTime())
          .slice(-8);

        const data = recent.map((entry, index) => {
          const previous = index > 0 ? recent[index - 1] : null;
          const pctChange =
            previous && previous.total > 0
              ? ((entry.total - previous.total) / previous.total) * 100
              : null;

          return { ...entry, pctChange };
        });

        const latestChange = data.length > 0 ? data[data.length - 1].pctChange : null;
        return { routine, data, latestChange };
      })
      .filter((entry) => entry.data.length > 0)
      .sort((a, b) => a.routine.localeCompare(b.routine, 'es'));
  }, [workouts]);

  const handleWeeklyCarouselScroll = useCallback(() => {
    const carousel = weeklyCarouselRef.current;
    if (!carousel) return;

    const cards = Array.from(
      carousel.querySelectorAll<HTMLElement>('[data-weekly-card="true"]')
    );
    if (cards.length === 0) return;

    const viewportCenter =
      carousel.getBoundingClientRect().left + carousel.clientWidth / 2;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      const distance = Math.abs(cardCenter - viewportCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveWeeklyIndex(closestIndex);
  }, []);

  const scrollToWeeklyIndex = useCallback((index: number) => {
    const carousel = weeklyCarouselRef.current;
    if (!carousel) return;

    const cards = Array.from(
      carousel.querySelectorAll<HTMLElement>('[data-weekly-card="true"]')
    );
    if (cards.length === 0) return;

    const nextIndex = Math.max(0, Math.min(cards.length - 1, index));
    cards[nextIndex]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest',
    });
    setActiveWeeklyIndex(nextIndex);
  }, []);

  useEffect(() => {
    if (routineWeeklyVolume.length === 0) {
      setActiveWeeklyIndex(0);
      return;
    }

    setActiveWeeklyIndex((current) =>
      Math.min(current, routineWeeklyVolume.length - 1)
    );
  }, [routineWeeklyVolume.length]);

  if (workouts.length === 0) {
    return (
      <div className='space-y-4 sm:space-y-6'>
        <div
          className={`animate-rise-in rounded-[30px] border p-8 text-center ${
            darkMode
              ? 'border-stone-800 bg-[#1a1713]'
              : 'border-stone-200 bg-white/70'
          }`}
        >
          <TrendingUp className='w-14 h-14 mx-auto mb-4 opacity-50' />
          <p className='text-xl font-semibold mb-5'>Sin entrenamientos todavía</p>
          <button
            onClick={() => setActiveTab('workout')}
            className='pressable inline-flex items-center gap-2 rounded-[18px] bg-gradient-to-r from-[#b4532a] to-[#d97745] px-5 py-3 text-sm font-semibold text-white'
          >
            Entrenar
            <ArrowRight className='w-4 h-4' />
          </button>
        </div>
      </div>
    );
  }

  const heroSurface = darkMode
    ? 'border-[#5a3925] bg-[linear-gradient(135deg,_rgba(36,28,22,0.95),_rgba(23,20,17,0.98))]'
    : 'border-[#e2c3ad] bg-[linear-gradient(135deg,_rgba(255,249,243,0.96),_rgba(247,238,228,0.98))]';

  return (
    <div className='space-y-5 sm:space-y-6'>
      <section className={`animate-rise-in rounded-[30px] border p-3 sm:p-4 ${heroSurface}`}>
        <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3'>
          <div className={`rounded-[22px] border p-3 ${theme.border} ${theme.card}`}>
            <p className={`text-[11px] uppercase tracking-[0.18em] ${theme.textSecondary}`}>Sesiones</p>
            <p className='mt-2 text-2xl font-bold'>{overview.totalWorkouts}</p>
            {overview.lastWorkoutDate && (
              <p className={`text-xs ${theme.textSecondary}`}>
                última {parseLocalDate(overview.lastWorkoutDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
              </p>
            )}
          </div>
          <div className={`rounded-[22px] border p-3 ${theme.border} ${theme.card}`}>
            <p className={`text-[11px] uppercase tracking-[0.18em] ${theme.textSecondary}`}>Volumen 28d</p>
            <p className='mt-2 text-2xl font-bold'>{overview.recentVolume.toFixed(0)} kg</p>
            {overview.volumeDelta !== null && (
              <p className={`text-xs ${overview.volumeDelta >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                {overview.volumeDelta >= 0 ? '+' : ''}{overview.volumeDelta.toFixed(0)}% vs 28d previos
              </p>
            )}
          </div>
          <div className={`rounded-[22px] border p-3 ${theme.border} ${theme.card} col-span-2 sm:col-span-1`}>
            <p className={`text-[11px] uppercase tracking-[0.18em] ${theme.textSecondary}`}>Días activos</p>
            <p className='mt-2 text-2xl font-bold'>{overview.activeDays}</p>
            {overview.streak > 0 && (
              <p className={`text-xs ${theme.textSecondary}`}>racha {overview.streak} {overview.streak === 1 ? 'día' : 'días'}</p>
            )}
          </div>
        </div>
      </section>

      <section className={`grid gap-4 ${topProgressors.length > 0 ? 'xl:grid-cols-[1.3fr_0.9fr]' : ''}`}>
        <article className={`rounded-[28px] border p-4 sm:p-5 ${theme.border} ${theme.card}`}>
          <div className='flex items-center justify-between gap-3 mb-4'>
            <h3 className='text-xl sm:text-2xl font-black tracking-[-0.04em]'>
              Ejercicio
            </h3>
            {selectedExercise && (
              <button
                type='button'
                onClick={() => setIsChartModalOpen(true)}
                className='pressable hidden sm:inline-flex items-center gap-2 rounded-[18px] bg-gradient-to-r from-[#b4532a] to-[#d97745] px-4 py-2.5 text-sm font-semibold text-white'
              >
                Ver gráficos
                <ArrowRight className='w-4 h-4' />
              </button>
            )}
          </div>

          <div className='relative'>
            <div className={`flex items-center gap-3 rounded-[22px] border px-4 py-3 ${theme.input}`}>
              <Search className={`w-4 h-4 ${theme.textSecondary}`} />
              <input
                type='text'
                value={exerciseSearch}
                onChange={(event) => {
                  setExerciseSearch(event.target.value.slice(0, 50));
                  setIsExerciseDropdownOpen(true);
                }}
                onFocus={() => setIsExerciseDropdownOpen(true)}
                onBlur={() => {
                  window.setTimeout(() => setIsExerciseDropdownOpen(false), 120);
                }}
                placeholder='Buscar ejercicio'
                maxLength={50}
                className='w-full bg-transparent outline-none text-base sm:text-sm'
              />
            </div>

            {isExerciseDropdownOpen && (
              <div className={`absolute z-20 mt-2 w-full rounded-[22px] border ${theme.border} ${theme.card} shadow-xl max-h-64 overflow-auto`}>
                {filteredExercises.length === 0 && (
                  <div className={`px-4 py-3 text-sm ${theme.textSecondary}`}>
                    Sin resultados
                  </div>
                )}
                {filteredExercises.map((exercise) => (
                  <button
                    key={exercise}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      setSelectedExercise(exercise);
                      setIsExerciseDropdownOpen(false);
                    }}
                    className={`pressable w-full text-left px-4 py-3 text-sm ${
                      selectedExercise === exercise
                        ? 'bg-[#d97745] text-white'
                        : theme.hover
                    }`}
                  >
                    {exercise}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className='mt-4 flex flex-wrap gap-2'>
            {exerciseMomentum.slice(0, 6).map((entry) => (
              <button
                key={entry.exerciseName}
                type='button'
                onClick={() => setSelectedExercise(entry.exerciseName)}
                className={`pressable rounded-full px-3 py-2 text-sm ${
                  selectedExercise === entry.exerciseName
                    ? 'bg-[#d97745] text-white'
                    : darkMode
                      ? 'bg-[#201b16] text-stone-300 hover:bg-[#2a241e]'
                      : 'bg-stone-200/80 text-stone-700 hover:bg-stone-300/80'
                }`}
              >
                {entry.exerciseName}
              </button>
            ))}
          </div>

          {selectedExercise && (
            <button
              type='button'
              onClick={() => setIsChartModalOpen(true)}
              className='pressable mt-4 inline-flex w-full sm:hidden items-center justify-center gap-2 rounded-[18px] bg-gradient-to-r from-[#b4532a] to-[#d97745] px-4 py-3 text-sm font-semibold text-white'
            >
              Ver gráficos
              <ArrowRight className='w-4 h-4' />
            </button>
          )}

          {selectedExercise && exerciseStats ? (
            <div className='mt-5 grid gap-3 sm:grid-cols-3'>
              <div className={`rounded-[22px] border p-4 ${darkMode ? 'border-stone-800 bg-[#17130f]' : 'border-stone-200 bg-stone-50/90'}`}>
                <p className={`text-[11px] uppercase tracking-[0.18em] ${theme.textSecondary}`}>Último peso</p>
                <p className='mt-2 text-2xl font-bold'>{exerciseStats.lastPeso.toFixed(1)} kg</p>
                <p className={`text-xs ${theme.textSecondary}`}>
                  {exerciseStats.diffPeso === null
                    ? '—'
                    : `${exerciseStats.diffPeso >= 0 ? '+' : ''}${exerciseStats.diffPeso.toFixed(1)} kg vs anterior`}
                </p>
              </div>
              <div className={`rounded-[22px] border p-4 ${darkMode ? 'border-stone-800 bg-[#17130f]' : 'border-stone-200 bg-stone-50/90'}`}>
                <p className={`text-[11px] uppercase tracking-[0.18em] ${theme.textSecondary}`}>1RM estimado</p>
                <p className='mt-2 text-2xl font-bold'>{exerciseStats.prOneRM.toFixed(1)} kg</p>
              </div>
              <div className={`rounded-[22px] border p-4 ${darkMode ? 'border-stone-800 bg-[#17130f]' : 'border-stone-200 bg-stone-50/90'}`}>
                <p className={`text-[11px] uppercase tracking-[0.18em] ${theme.textSecondary}`}>PR de volumen</p>
                <p className='mt-2 text-2xl font-bold'>{exerciseStats.prVolumen.toFixed(0)} kg</p>
                <p className={`text-xs ${theme.textSecondary}`}>
                  {exerciseStats.diffVolume === null
                    ? '—'
                    : `${exerciseStats.diffVolume >= 0 ? '+' : ''}${exerciseStats.diffVolume.toFixed(0)} kg última sesión vs anterior`}
                </p>
              </div>
            </div>
          ) : (
            <div className={`mt-5 rounded-[22px] border border-dashed p-5 text-sm ${theme.textSecondary} ${theme.border}`}>
              Elegí un ejercicio
            </div>
          )}
        </article>

        {topProgressors.length > 0 && (
        <article className={`rounded-[28px] border p-4 sm:p-5 ${theme.border} ${theme.card}`}>
          <h3 className='text-xl sm:text-2xl font-black tracking-[-0.04em]'>
            Mejor progresión
          </h3>
          <div className='mt-4 space-y-3'>
            {topProgressors.map((entry, index) => (
              <button
                key={entry.exerciseName}
                type='button'
                onClick={() => setSelectedExercise(entry.exerciseName)}
                className={`pressable lift-card w-full rounded-[22px] border p-4 text-left ${
                  index === 0
                    ? darkMode
                      ? 'border-[#5a3925] bg-[#241811]'
                      : 'border-[#e2c3ad] bg-[#fff5ee]'
                    : `${theme.border} ${darkMode ? 'bg-[#17130f]' : 'bg-stone-50/90'}`
                }`}
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='min-w-0'>
                    <p className='font-semibold truncate'>{entry.exerciseName}</p>
                    <p className={`mt-1 text-sm ${theme.textSecondary}`}>
                      {entry.sessions} sesiones · {entry.lastWeight.toFixed(1)} kg
                    </p>
                  </div>
                  <div className='rounded-2xl bg-emerald-600/15 px-3 py-2 text-right text-emerald-500'>
                    <p className='text-[11px] uppercase tracking-[0.18em]'>1RM</p>
                    <p className='text-lg font-bold'>
                      +{entry.deltaOneRM.toFixed(1)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </article>
        )}
      </section>

      {routineWeeklyVolume.length > 0 && (
        <section className='relative'>
          <div className='mb-3 flex items-center justify-between px-1'>
            <h3 className='text-xl font-black tracking-[-0.04em]'>
              Volumen semanal
            </h3>
            <span className={`text-xs ${theme.textSecondary}`}>
              {activeWeeklyIndex + 1}/{routineWeeklyVolume.length}
            </span>
          </div>

          <div
            ref={weeklyCarouselRef}
            onScroll={handleWeeklyCarouselScroll}
            className='routine-carousel flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-1 pb-2 pt-1'
            aria-label='Carrusel de volumen semanal'
          >
            {routineWeeklyVolume.map((routine) => (
              <article
                data-weekly-card='true'
                key={routine.routine}
                className={`lift-card snap-start shrink-0 w-full lg:w-[calc((100%-2rem)/3)] rounded-[28px] border p-4 sm:p-5 ${
                  darkMode
                    ? 'border-stone-800 bg-[#1a1713]'
                    : 'border-white/80 bg-white/75'
                }`}
              >
                <div className='mb-4 flex items-center justify-between gap-3'>
                  <div className='min-w-0'>
                    <h4 className='text-lg font-semibold truncate'>{routine.routine}</h4>
                  </div>
                  {routine.latestChange !== null && (
                    <div className={`rounded-[18px] px-3 py-2 ${
                      routine.latestChange >= 0
                        ? 'bg-emerald-600/15 text-emerald-500'
                        : 'bg-red-600/15 text-red-400'
                    }`}>
                      <p className='text-[11px] uppercase tracking-[0.18em]'>Última</p>
                      <p className='text-lg font-bold'>
                        {routine.latestChange > 0 ? '+' : ''}
                        {routine.latestChange.toFixed(0)}%
                      </p>
                    </div>
                  )}
                </div>

                <ResponsiveContainer width='100%' height={220}>
                  <BarChart
                    data={routine.data}
                    barCategoryGap='25%'
                    accessibilityLayer={false}
                    tabIndex={-1}
                  >
                    <CartesianGrid
                      strokeDasharray='3 3'
                      stroke={darkMode ? '#44403c' : '#d6d3d1'}
                    />
                    <XAxis
                      dataKey='label'
                      stroke={darkMode ? '#a8a29e' : '#78716c'}
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor='end'
                      height={60}
                    />
                    <YAxis
                      stroke={darkMode ? '#a8a29e' : '#78716c'}
                      tick={{ fontSize: 10 }}
                    />
                    <Tooltip
                      trigger='hover'
                      wrapperStyle={{ pointerEvents: 'none' }}
                      formatter={(value: number) => [`${value.toFixed(0)} kg`, 'Volumen']}
                      cursor={{ fill: 'transparent' }}
                      contentStyle={{
                        backgroundColor: darkMode ? '#1c1915' : '#ffffff',
                        border: `1px solid ${darkMode ? '#44403c' : '#d6d3d1'}`,
                        borderRadius: '16px',
                        color: darkMode ? '#f5f5f4' : '#1c1917',
                        fontSize: '12px',
                      }}
                    />
                    <Bar
                      dataKey='total'
                      fill='#d97745'
                      radius={[8, 8, 0, 0]}
                      barSize={18}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </article>
            ))}
          </div>

          {routineWeeklyVolume.length > 1 && (
            <div className='mt-3 flex items-center justify-center gap-2'>
              {routineWeeklyVolume.map((routine, index) => (
                <button
                  key={`weekly-dot-${routine.routine}-${index}`}
                  type='button'
                  onClick={() => scrollToWeeklyIndex(index)}
                  className={`h-2.5 rounded-full transition-all ${
                    activeWeeklyIndex === index
                      ? 'w-8 bg-[#d97745]'
                      : `w-2.5 ${theme.border} border ${theme.card}`
                  }`}
                  aria-label={`Ir al gráfico semanal ${index + 1}`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {selectedExercise && isChartModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3 sm:p-4'>
          <div
            className={`animate-rise-in w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-[30px] border p-4 sm:p-6 ${
              darkMode
                ? 'border-stone-800 bg-[linear-gradient(180deg,rgba(28,25,21,0.98),rgba(18,16,13,0.98))]'
                : 'border-[#e2c3ad] bg-[linear-gradient(180deg,rgba(255,249,243,0.98),rgba(247,238,228,0.98))]'
            }`}
          >
            <div className='flex items-center justify-between gap-3 mb-5'>
              <h3 className='text-2xl font-black tracking-[-0.04em]'>
                {selectedExercise}
              </h3>
              <button
                onClick={() => setIsChartModalOpen(false)}
                className={`pressable rounded-2xl p-2 ${theme.hover}`}
                aria-label='Cerrar gráfico'
              >
                <X className='w-5 h-5' />
              </button>
            </div>

            <div className='grid gap-4 sm:grid-cols-3 mb-6'>
              <div className={`rounded-[22px] border p-4 ${theme.border} ${theme.card}`}>
                <p className={`text-[11px] uppercase tracking-[0.18em] ${theme.textSecondary}`}>PR de peso</p>
                <p className='mt-2 text-2xl font-bold'>{exerciseStats?.prPeso.toFixed(1) ?? '0.0'} kg</p>
              </div>
              <div className={`rounded-[22px] border p-4 ${theme.border} ${theme.card}`}>
                <p className={`text-[11px] uppercase tracking-[0.18em] ${theme.textSecondary}`}>PR 1RM</p>
                <p className='mt-2 text-2xl font-bold'>{exerciseStats?.prOneRM.toFixed(1) ?? '0.0'} kg</p>
              </div>
              <div className={`rounded-[22px] border p-4 ${theme.border} ${theme.card}`}>
                <p className={`text-[11px] uppercase tracking-[0.18em] ${theme.textSecondary}`}>PR volumen</p>
                <p className='mt-2 text-2xl font-bold'>{exerciseStats?.prVolumen.toFixed(0) ?? '0'} kg</p>
              </div>
            </div>

            <div className='mb-6'>
              <h4 className={`text-xs font-semibold uppercase tracking-[0.18em] mb-3 ${theme.textSecondary}`}>
                Peso máximo y 1RM estimado
              </h4>
              <ResponsiveContainer width='100%' height={220}>
                <LineChart
                  data={selectedExerciseData}
                  accessibilityLayer={false}
                  tabIndex={-1}
                >
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke={darkMode ? '#44403c' : '#d6d3d1'}
                  />
                  <XAxis
                    dataKey='date'
                    stroke={darkMode ? '#a8a29e' : '#78716c'}
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor='end'
                    height={60}
                  />
                  <YAxis
                    stroke={darkMode ? '#a8a29e' : '#78716c'}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip
                    trigger='hover'
                    wrapperStyle={{ pointerEvents: 'none' }}
                    formatter={(value: number, name: string) => {
                      if (name === '1RM Estimado') return [`${value.toFixed(1)} kg`, name];
                      return [`${value.toFixed(1)} kg`, 'Peso Máximo'];
                    }}
                    contentStyle={{
                      backgroundColor: darkMode ? '#1c1915' : '#ffffff',
                      border: `1px solid ${darkMode ? '#44403c' : '#d6d3d1'}`,
                      borderRadius: '16px',
                      color: darkMode ? '#f5f5f4' : '#1c1917',
                      fontSize: '12px',
                    }}
                  />
                  <Line
                    type='monotone'
                    dataKey='peso'
                    name='Peso Máximo'
                    stroke='#d97745'
                    strokeWidth={3}
                  />
                  <Line
                    type='monotone'
                    dataKey='oneRM'
                    name='1RM Estimado'
                    stroke='#84cc16'
                    strokeWidth={2}
                    strokeDasharray='6 4'
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div>
              <h4 className={`text-xs font-semibold uppercase tracking-[0.18em] mb-3 ${theme.textSecondary}`}>
                Volumen total
              </h4>
              <ResponsiveContainer width='100%' height={220}>
                <LineChart
                  data={selectedExerciseData}
                  accessibilityLayer={false}
                  tabIndex={-1}
                >
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke={darkMode ? '#44403c' : '#d6d3d1'}
                  />
                  <XAxis
                    dataKey='date'
                    stroke={darkMode ? '#a8a29e' : '#78716c'}
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor='end'
                    height={60}
                  />
                  <YAxis
                    stroke={darkMode ? '#a8a29e' : '#78716c'}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip
                    trigger='hover'
                    wrapperStyle={{ pointerEvents: 'none' }}
                    formatter={(value: number) => [`${value.toFixed(0)} kg`, 'Volumen']}
                    contentStyle={{
                      backgroundColor: darkMode ? '#1c1915' : '#ffffff',
                      border: `1px solid ${darkMode ? '#44403c' : '#d6d3d1'}`,
                      borderRadius: '16px',
                      color: darkMode ? '#f5f5f4' : '#1c1917',
                      fontSize: '12px',
                    }}
                  />
                  <Line
                    type='monotone'
                    dataKey='volumen'
                    stroke='#38bdf8'
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
